import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView } from 'react-native';

import { useNavigation } from '@react-navigation/native';

import { Button } from '@/components/ButtonComponent';
import { ErrorMessagePayment } from '@/components/ErrorMessagePayment';
import { NumberPad } from '@/components/NumberPad';
import { Screen } from '@/components/ScreenComponent';
import { ScreenNavBar } from '@/components/ScreenNavBar';
import { Box } from '@/components/lib/components';
import { PaymentMethodTypes } from '@/feature/deposit/const';
import { useDepositPaymentMethods } from '@/feature/deposit/hooks/use-deposit-payment-methods';
import { MaxWidthWrapper } from '@/feature/responsive-design/WebComponents';
import { useCurrencyNumberPad } from '@/hooks/use-currency-number-pad';
import { RedeemToFantasyFormNavigationProp } from '@/navigation/withdraw/types';
import { common } from '@/styles/styles';
import { logger } from '@/utils/logging';
import { toLocaleCurrency } from '@/utils/numeric/currency';
import { toFraction } from '@/utils/numeric/fraction';

import { WithdrawalNumberSection } from '../components/WithdrawalNumberSection';
import { useRedeemToFantasy } from '../hooks/use-redeem-to-fantasy';
import { useSelectedWithdrawalMethodStore } from '../hooks/use-selected-withdrawal-method';
import { WITHDRAWAL_LIMITS, getErrorMessage } from '../utils/utils';

const LOG_TAG = `[Redeem to Fantasy]`;

export const RedeemToFantasyForm = () => {
    const navigation = useNavigation<RedeemToFantasyFormNavigationProp>();
    const { t } = useTranslation(['transactions', 'wallet']);
    const { data: depositPaymentMethods } = useDepositPaymentMethods(); // useDepositPaymentMethods returning all wallets
    const sweepsCashWallet = depositPaymentMethods?.sweeps_cash_wallet;

    const { mutateAsync: redeemToFantasy, isPending } = useRedeemToFantasy();

    const maxWithdrawalLimit = WITHDRAWAL_LIMITS.maxSweepsWithdrawalLimit;
    const minWithdrawalLimit = WITHDRAWAL_LIMITS.minWithdrawalLimit;
    const balance = toFraction(sweepsCashWallet?.real_amount);

    const [amountToWithdraw, setAmountToWithdraw] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [isEditing, setIsEditing] = useState(true);

    const { displayValue, number, onNumberPress, onDecimalPress, onBackspacePress } = useCurrencyNumberPad(
        Number(amountToWithdraw)
    );

    const amountToWithdrawNum = Number(amountToWithdraw);
    const isValid = amountToWithdrawNum > 0 && amountToWithdrawNum <= balance && !errorMessage;
    const clearSelection = useSelectedWithdrawalMethodStore(state => state.clearSelection);

    useEffect(() => {
        const numberString = number.toString();
        setAmountToWithdraw(numberString);
        const message = getErrorMessage(
            parseFloat(maxWithdrawalLimit),
            numberString,
            balance,
            true,
            parseFloat(minWithdrawalLimit)
        );
        setErrorMessage(message);
    }, [number, balance, minWithdrawalLimit, maxWithdrawalLimit]);

    const onWithdrawPress = async () => {
        try {
            await redeemToFantasy(amountToWithdrawNum);
            navigation.navigate('SuccessfulWithdrawal', {
                amountToWithdraw: amountToWithdrawNum,
                methodType: PaymentMethodTypes.FantasyMoney,
                title: t('wallet:fantasy_money_successful_redemption_title'),
                description: t('wallet:fantasy_money_successful_redemption', {
                    amount: toLocaleCurrency(amountToWithdrawNum),
                }),
            });
        } catch (error) {
            navigation.navigate('FailedWithdrawal', { errorCode: undefined });
            logger.error(LOG_TAG, 'Redeem to Fantasy failed: ', error);
        } finally {
            clearSelection();
        }
    };

    return (
        <Screen>
            <ScreenNavBar title={t('redeem_to_fantasy')} subTitle={t('redeem_to_fantasy_description')} />
            <ScrollView contentContainerStyle={[common.grow, common.justifyCenter, common.paddingHorizontal]}>
                <MaxWidthWrapper>
                    <WithdrawalNumberSection
                        {...{
                            isEditing,
                            setIsEditing,
                            displayValue,
                        }}
                    />
                </MaxWidthWrapper>
            </ScrollView>
            <MaxWidthWrapper>
                <NumberPad
                    relativePosition
                    toolbar={
                        <Box pb="s16">
                            <ErrorMessagePayment {...{ errorMessage }} />
                            <Box paddingHorizontal="s16">
                                <Button
                                    label={`Redeem ${toLocaleCurrency(amountToWithdrawNum)}`}
                                    hierarchy={'primary'}
                                    disabled={!isValid || isPending}
                                    onPress={onWithdrawPress}
                                    testID="withdrawBtn"
                                />
                            </Box>
                        </Box>
                    }
                    {...{ onNumberPress, onDecimalPress, onBackspacePress }}
                />
            </MaxWidthWrapper>
        </Screen>
    );
};
